import {
  GMAX_CATCH_OVERKILL_PER_RATIO,
  GMAX_CATCH_PITY_PER_STACK,
} from './gmax_constants.js'

export type GmaxRaidDifficulty = 'easy' | 'medium' | 'hard' | 'extreme'

export type GmaxRaidDef = {
  slug: string
  baseSlug: string
  requiredDps: number
  costParticles: number
  fragmentsOnWin: number
  fragmentsOnLoss: number
  guaranteedFragments: number
  pityRequired: number
  baseCatchRate: number
  maxCatchRate: number
}

export function estimateGmaxCatchRate(
  raid: Pick<GmaxRaidDef, 'requiredDps' | 'baseCatchRate' | 'maxCatchRate'>,
  teamDps: number,
  pity: number
): number {
  if (teamDps < raid.requiredDps) return 0
  const overkill = Math.max(0, teamDps / Math.max(1, raid.requiredDps) - 1)
  const pityBonus = Math.max(0, pity) * GMAX_CATCH_PITY_PER_STACK
  return Math.min(
    raid.maxCatchRate,
    raid.baseCatchRate + overkill * GMAX_CATCH_OVERKILL_PER_RATIO + pityBonus
  )
}

export type GmaxDenProgress = {
  fragments: number
  captures: number
}

/**
 * Antre « terminée » pour les raids normaux : la forme Gigamax est déjà débloquée.
 * Les ★ se montent sur le Pokémon de base (gacha / pension) ; la forme est cosmétique.
 * Le craft garanti reste disponible via `canCraftGmaxDen` tant que la forme n’est pas débloquée.
 */
export function isGmaxDenRaidLocked(formUnlocked: boolean): boolean {
  return formUnlocked
}

/** Meilleur nombre d’étoiles sur l’espèce de base (hors shiny), toutes formes confondues. */
export function maxStarsForBaseSlug(
  collection: ReadonlyArray<{ slug: string; isShiny: boolean; stars: number }>,
  baseSlug: string
): number {
  let max = 0
  for (const p of collection) {
    if (p.isShiny || p.slug !== baseSlug) continue
    max = Math.max(max, Math.max(0, Math.floor(Number(p.stars) || 0)))
  }
  return max
}

export function canCraftGmaxDen(
  progress: Pick<GmaxDenProgress, 'fragments'>,
  guaranteedFragments: number
): boolean {
  return (
    Math.max(0, Math.floor(Number(progress.fragments ?? 0))) >=
    Math.max(1, Math.floor(Number(guaranteedFragments)))
  )
}

export type GmaxRaidAttemptOutcome = {
  won: boolean
  captured: boolean
  catchRate: number
  fragmentsEarned: number
  pityTriggered: boolean
  fragmentsTriggered: boolean
  /** Tirage RNG capture (0–1), absent si pas de victoire. */
  captureRoll?: number
}

/**
 * Résout un raid (RNG fourni pour tests).
 * Ne modifie pas l'état ; le service persiste particules / pity / fragments.
 */
export function resolveGmaxRaidAttempt(
  raid: GmaxRaidDef,
  teamDps: number,
  progress: { fragments: number; pity: number },
  rng: () => number = Math.random
): GmaxRaidAttemptOutcome {
  const won = teamDps >= raid.requiredDps
  const fragmentsEarned = won ? raid.fragmentsOnWin : raid.fragmentsOnLoss
  let catchRate = 0
  let pityTriggered = false
  let fragmentsTriggered = false
  let captured = false
  let captureRoll: number | undefined

  if (won) {
    const pityAfter = progress.pity + 1
    catchRate = estimateGmaxCatchRate(raid, teamDps, pityAfter)
    pityTriggered = pityAfter >= raid.pityRequired
    const fragmentsAfter = Math.min(999, progress.fragments + fragmentsEarned)
    fragmentsTriggered = fragmentsAfter >= raid.guaranteedFragments
    captureRoll = rng()
    captured = pityTriggered || fragmentsTriggered || captureRoll < catchRate
  }

  return {
    won,
    captured,
    catchRate,
    fragmentsEarned,
    pityTriggered,
    fragmentsTriggered,
    captureRoll,
  }
}
